import { NetworkStatus } from "@apollo/client";
import {
  useLazyQuery,
  useQuery
} from "@apollo/client/react";
import { DocumentNode } from "graphql";
import { useCallback, useEffect, useRef, useState } from "react";

// Common Apollo Callbacks
export type ApolloCallbacks<TData> = {
  onCompleted?: (data: TData) => void;
  onError?: (error: Error) => void;
};

// UseQuery with callbacks
export type UseQueryOptions<TData, TVariables> =
  useQuery.Options<TData, TVariables> &
  ApolloCallbacks<TData>;

export function useQueryWithCallbacks<TData = any, TVariables = Record<string, any>>(
  query: DocumentNode,
  options?: UseQueryOptions<TData, TVariables>
): useQuery.Result<TData, TVariables> {
  const result = useQuery<TData, TVariables>(query, options);

  const completedRef = useRef(false);
  const errorRef = useRef(false);
  
  useEffect(() => {
    if (result.networkStatus === NetworkStatus.refetch) {
      completedRef.current = false;
      errorRef.current = false;
    }
  }, [result.networkStatus]);

  useEffect(() => {
    if (!completedRef.current && result.data && !result.loading) {
      completedRef.current = true;
      options?.onCompleted?.(result.data);
    }
  }, [result.data, result.loading, options]);

  useEffect(() => {
    if (!errorRef.current && result.error && !result.loading) {
      errorRef.current = true;
      options?.onError?.(result.error);
    }
  }, [result.error, result.loading, options]);

  return result;
}

// UseLazyQuery with callbacks
export type UseLazyQueryOptions<TData, TVariables> =
  useLazyQuery.Options<TData, TVariables> &
  ApolloCallbacks<TData>;

export function useLazyQueryWithCallbacks<TData = any, TVariables = Record<string, any>>(
  query: DocumentNode,
  options?: UseLazyQueryOptions<TData, TVariables>
) {
  const [executeOriginal, result] = useLazyQuery<TData, TVariables>(query, options);

  const completedRef = useRef(false);
  const errorRef = useRef(false);
  
  const execute = useCallback(
    (execOptions?: any) => {
      completedRef.current = false;
      errorRef.current = false;

      return executeOriginal(execOptions);
    },
    [executeOriginal]
  );

  useEffect(() => {
    if (!completedRef.current && result.data && !result.loading) {
      completedRef.current = true;
      options?.onCompleted?.(result.data);
    }
  }, [result.data, result.loading]);

  useEffect(() => {
    if (!errorRef.current && result.error && !result.loading) {
      errorRef.current = true;
      options?.onError?.(result.error);
    }
  }, [result.error, result.loading]);

  return [execute, result] as const;
}